import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { 
  Activity, 
  Thermometer, Droplet, Brain, Home, Users, 
  FileEdit, FileText, Stethoscope, Eye, 
  FileBarChart, Shield, Baby, User,
  ChevronDown, ChevronUp, AlertTriangle, Heart, CheckCircle,
  ClipboardList, LogOut, RefreshCw, ChevronsUp, ChevronsDown
} from 'lucide-react';
import PrimaryButton from '../../common/PrimaryButton';
import { useFeature } from '../../../hooks/useFeature';
import AdultTransitionToggle from './AdultTransitionToggle';
import { withPediatricFirstConfig, patientTypeLabel } from './assessmentFormConfig';
import ICDSelect from '../../common/ICDSelect';
import { joinIcd10Field, splitIcd10Field } from '../../../utils/icd10Entries';

// Assessment Type definitions
type AssessmentType = 'initial' | 'recertification' | 'discharge';

interface AssessmentTypeConfig {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface FieldConfig {
  name: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'checkbox';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  // Visibility control: which assessment types should show this field
  showFor?: AssessmentType[];
  // If not specified, shows for all types
}

const ASSESSMENT_TYPES: Record<AssessmentType, AssessmentTypeConfig> = {
  initial: {
    label: 'Initial/Admission Assessment (SOC)',
    description: 'Comprehensive assessment for Start of Care. All baseline data collected.',
    icon: <ClipboardList size={16} />,
    color: 'blue'
  },
  recertification: {
    label: 'Recertification Assessment',
    description: 'Periodic reassessment (60-90 days). Focus on changes and continued medical necessity.',
    icon: <RefreshCw size={16} />,
    color: 'amber'
  },
  discharge: {
    label: 'Discharge/End of Care Assessment',
    description: 'Final assessment documenting outcomes, discharge status, and follow-up plans.',
    icon: <LogOut size={16} />,
    color: 'green'
  }
};

export interface AssessmentFormHandle {
  /** Save the current form as a DRAFT (no required-field check) */
  triggerSave: () => void;
  /** Required-field and rule check used before Submit; returns messages, empty when valid */
  validate: () => string[];
  /** The payload onSave would receive, for autosave */
  getSaveData: () => Record<string, unknown>;
  /** Serialised form state for dirty tracking */
  getSnapshot: () => string;
}

interface NursingAssessmentFormProps {
  editingId: string | null;
  patientId: string;
  onSave: (data: any) => void;
  existingData?: any;
  assessmentStatus?: string;
  isReadOnly?: boolean;
}

const NursingAssessmentForm = forwardRef<AssessmentFormHandle, NursingAssessmentFormProps>(
  ({ editingId, patientId, onSave, existingData = null, assessmentStatus, isReadOnly = false }, ref) => {
  // Phase 3: Medicare content (face-to-face, Medicare wording) is hidden unless the medicareContent flag is on
  const medicareEnabled = useFeature('medicareContent');
  // Default form configuration
  const defaultFormConfig = {
    assessmentType: 'initial' as AssessmentType,
    patientType: 'pediatric', // Phase 19 F26: 'pediatric' or 'adult' (transition, 18–21 toggle)
    settingType: 'home-health', // Phase 19 F26: always home health (no selector)
    nursingLevel: 'private-duty', // Phase 19 F26: always PDN (no selector)
    showOptionalSections: false
  };

  // Helper to normalize assessment type from backend format to internal format
  const normalizeAssessmentType = (type: string): AssessmentType => {
    const typeMap: Record<string, AssessmentType> = {
      'START_OF_CARE': 'initial',
      'start_of_care': 'initial',
      'initial': 'initial',
      'REASSESSMENT': 'recertification',
      'reassessment': 'recertification',
      'recertification': 'recertification',
      'END_OF_CARE': 'discharge',
      'end_of_care': 'discharge',
      'discharge': 'discharge'
    };
    return typeMap[type] || 'initial';
  };

  // Form configuration state - MVP defaults to pediatric PDN
  const [formConfig, setFormConfig] = useState(() => {
    // If editing existing data with formConfig, use that immediately
    if (existingData && existingData.formConfig) {
      const config = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      config.assessmentType = normalizeAssessmentType(config.assessmentType);
      return config;
    }
    // Otherwise use defaults for new form
    return defaultFormConfig;
  });

  // Medical codes state
  const [secondaryICD10Codes, setSecondaryICD10Codes] = useState<string[]>([]);
  const [selectedSecondaryICD10, setSelectedSecondaryICD10] = useState<string>('');

  // Track if initial data has been loaded to prevent overwriting
  const initialLoadCompleteRef = useRef(false);

  // Collapsible sections state
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'Recertification Summary': true,
    'Discharge Summary': true,
    'Diagnosis and Coding': true,
    'Home Health Certification': true,
    'Pediatric Information': true,
    'Pediatric PDN Specialized Care': true,
    'Nursing Assessment': true,
    'Physical Assessment': true,
    'Mobility Assessment': true,
    'Functional Limitations': true,
    'Activities of Daily Living (Age-Appropriate)': true,
    'Communication Assessment': true,
    'Neurological & Cognitive Assessment': true,
    'Durable Medical Equipments (DME)': true,
    'Home Environment Assessment': true,
    'Caregiver Assessment': true,
    'Assessment Attestation': true,
    'Nutritional Assessment': true,
    'Psychosocial Assessment': true,
    'Advanced Assessment': true
  });
  const [, setAllSectionsCollapsed] = useState(true);

  useEffect(() => {
    // Dynamically check all currently rendered categories
    const allCategoryNames = getAssessmentCategories().map(cat => cat.name);
    const allCollapsed = allCategoryNames.every(
      (name) => collapsedSections[name] === true
    );
    setAllSectionsCollapsed(allCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getAssessmentCategories is recreated every render; the listed deps are the inputs that change the categories
  }, [collapsedSections, formConfig]);




  // Helper to check if a field should be visible based on assessment type
  const shouldShowField = (field: FieldConfig): boolean => {
    if (!field.showFor) return true; // No restriction = show for all
    return field.showFor.includes(formConfig.assessmentType);
  };

  // Helper to get dynamic placeholder based on assessment type
  const getPlaceholder = (field: FieldConfig): string => {
    const { assessmentType } = formConfig;
    
    // Dynamic placeholders for recertification
    if (assessmentType === 'recertification') {
      const recertPlaceholders: Record<string, string> = {
        'Chief Complaint': 'Current primary concerns or changes since last assessment',
        'History of Present Illness': 'Update on current medical condition and any changes since last assessment',
        'Past Medical History': 'Any new diagnoses or conditions since last assessment',
        'Surgical History': 'Any new surgeries since last assessment',
        'Homebound Status Justification': 'Confirm continued homebound status. Document any changes since last assessment.',
        'Recommended Skilled Nursing Frequency': 'e.g., Continue 16 hours/day PDN (no change) OR Change to 12 hours/day',
        'Frequency Justification': 'Justify continued or changed frequency based on current patient condition',
        'Developmental Milestones': 'Document any new milestones achieved or changes since last assessment',
        'Ventilator Settings': 'Current settings (note any changes from last assessment)',
        'Emergency Equipment Notes': 'Document any changes to equipment status since last assessment',
        'Responsible Adult Emergency Skills Verified': 'Verify caregiver maintains competency. Note any retraining provided.',
        'PDN Hours Justification': 'Justify continued or changed PDN hours based on current needs',
        'Education Provided': 'Specific education provided during this recertification visit'
      };
      if (recertPlaceholders[field.name]) return recertPlaceholders[field.name];
    }
    
    // Dynamic placeholders for discharge
    if (assessmentType === 'discharge') {
      const dischargePlaceholders: Record<string, string> = {
        'Assessment Summary': 'Brief summary of patient status at discharge',
        'Recommended Nursing Interventions': 'Summary of interventions provided during episode of care',
        'Priority Problems Identified': 'Problems addressed during episode and current status',
        'Referrals Recommended': 'Referrals made during episode and any outstanding referrals',
        'Education Provided': 'Summary of education provided during episode of care',
        'Caregiver Teaching Needs': 'Any outstanding teaching needs at discharge'
      };
      if (dischargePlaceholders[field.name]) return dischargePlaceholders[field.name];
    }
    
    return field.placeholder || `Enter ${field.name}...`;
  };

  // Get assessment categories based on configuration
  const getAssessmentCategories = () => {
    const categories = [];
    const { assessmentType } = formConfig;

    // ========== RECERTIFICATION SUMMARY (only for recert) ==========
    if (assessmentType === 'recertification') {
      categories.push({
        name: "Recertification Summary",
        priority: 0.5,
        required: true,
        description: "Document changes since last assessment and justification for continued services.",
        fields: [
          { name: "Previous Assessment Date", type: "date", required: true },
          { name: "Recertification Period Start", type: "date", required: true },
          { name: "Recertification Period End", type: "date", required: true },
          { name: "Changes Since Last Assessment", type: "textarea", required: true,
            placeholder: "Document all changes in patient condition, medications, equipment, caregiver status, or living situation since the last assessment." },
          { name: "Continued Medical Necessity Justification", type: "textarea", required: true,
            placeholder: "Explain why skilled nursing services continue to be medically necessary. Include: ongoing complex care needs, patient's inability to self-manage, safety concerns." },
          { name: "Goal Progress Summary", type: "textarea", required: true,
            placeholder: "For each care plan goal, document:\n- Goal description\n- Progress status (Met, Partially Met, Not Met, Ongoing)\n- Evidence of progress or barriers" },
          { name: "New Problems Identified", type: "textarea",
            placeholder: "Document any new diagnoses, complications, or care needs identified" },
          { name: "Physician Contact for Recertification", type: "select", required: true,
            options: ["Physician contacted - verbal orders obtained", "Physician contacted - awaiting response", "Face-to-face encounter completed", "Telehealth encounter completed", "Orders received via fax/portal"] },
          { name: "Physician Contact Date", type: "date" },
          { name: "Updated Orders Received", type: "select",
            options: ["No changes to orders", "Orders updated - see details", "Pending physician signature"] },
          { name: "Order Changes Description", type: "textarea", placeholder: "If orders were updated, describe the changes" }
        ]
      });
    }

    // ========== DISCHARGE SUMMARY (only for discharge) ==========
    if (assessmentType === 'discharge') {
      categories.push({
        name: "Discharge Summary",
        priority: 0.5,
        required: true,
        description: "Complete discharge documentation including outcomes, instructions, and follow-up plans.",
        fields: [
          { name: "Discharge Date", type: "date", required: true },
          { name: "Original Start of Care Date", type: "date", required: true },
          { name: "Total Length of Service", type: "text", placeholder: "e.g., 6 months, 2 years" },
          { name: "Discharge Reason", type: "select", required: true,
            options: ["Goals met - services no longer needed", "Patient/family request", "Moved out of service area", "Transferred to another agency", 
                      "Transferred to higher level of care (hospital)", "Transferred to facility (SNF/LTAC)", "Patient deceased", 
                      "Insurance/authorization ended", "Non-compliance with care plan", "Safety concerns - unable to provide care", "Physician discharged from services", "Other - see notes"] },
          { name: "Discharge Reason Details", type: "textarea", placeholder: "Provide additional details about the discharge reason" },
          { name: "Discharge Disposition", type: "select", required: true,
            options: ["Home - self care", "Home - with family/caregiver support", "Home - with outpatient services", "Home - with hospice services",
                      "Acute care hospital", "Skilled nursing facility", "Long-term acute care (LTAC)", "Rehabilitation facility", "Assisted living", "Deceased", "Other"] },
          { name: "Goals Met Summary", type: "textarea", required: true,
            placeholder: "For each care plan goal, document final status:\n- Goal 1: [Met/Partially Met/Not Met] - [Brief explanation]\n- Goal 2: [Met/Partially Met/Not Met] - [Brief explanation]" },
          { name: "Clinical Outcomes Summary", type: "textarea", required: true,
            placeholder: "Summarize the patient's clinical status at discharge compared to admission. Include improvements, stable conditions, and any decline." },
          { name: "Discharge Instructions Provided", type: "select", required: true,
            options: ["Yes - verbal and written instructions provided", "Yes - verbal instructions only", "Yes - written instructions only", 
                      "No - patient declined", "No - patient unable to receive (cognitive/medical)", "N/A - patient deceased"] },
          { name: "Discharge Instructions Details", type: "textarea",
            placeholder: "List specific instructions provided:\n- Medication management\n- Diet/nutrition\n- Activity level\n- Warning signs to watch for" },
          { name: "Patient/Caregiver Verbalized Understanding", type: "select", required: true,
            options: ["Yes - demonstrated full understanding", "Yes - with some clarification needed", "Partial understanding - reinforced teaching", 
                      "Limited understanding - referred to physician", "Unable to assess", "N/A - patient deceased"] },
          { name: "Follow-up Plan", type: "textarea", required: true,
            placeholder: "Document follow-up appointments, referrals, and ongoing care:\n- PCP follow-up: Date/timeframe\n- Specialist appointments:\n- Therapy services:" },
          { name: "Follow-up Appointments Scheduled", type: "textarea", placeholder: "List specific appointments already scheduled with dates" },
          { name: "Equipment Disposition", type: "select",
            options: ["N/A - no equipment", "Equipment to remain with patient", "Equipment returned to DME company", "Equipment transferred with patient", 
                      "Pending pickup - scheduled", "Pending pickup - needs scheduling"] },
          { name: "Equipment Details", type: "textarea", placeholder: "List equipment and disposition for each item" },
          { name: "Medication Reconciliation Completed", type: "select",
            options: ["Yes - reconciled with PCP", "Yes - reconciled with pharmacy", "Pending - sent to PCP for review", "No - patient/family to follow up", "N/A"] },
          { name: "Discharge Nursing Summary", type: "textarea", required: true,
            placeholder: "Comprehensive narrative summary of the patient's episode of care, interventions provided, response to treatment, and current status at discharge." },
          { name: "Physician Notified of Discharge", type: "select", required: true,
            options: ["Yes - verbal notification", "Yes - written/fax notification", "Yes - via EMR/portal", "Pending notification", "N/A - physician ordered discharge"] },
          { name: "Physician Notification Date", type: "date" },
          { name: "Transfer Summary Sent", type: "select",
            options: ["N/A - not transferring", "Yes - sent to receiving facility/agency", "Yes - sent to PCP", "Pending - to be sent", "Patient declined release of records"] },
          { name: "Receiving Agency Name", type: "text",
            placeholder: "Name of agency/facility patient is transferring to (if applicable)" },
          { name: "Receiving Agency Contact", type: "text",
            placeholder: "Phone number or contact person at receiving agency" },
          { name: "Continuity of Care Notes", type: "textarea",
            placeholder: "Critical information for receiving provider: current care needs, equipment requirements, caregiver training status, pending issues" },
          { name: "Services Provided Summary", type: "textarea",
            placeholder: "Total visits/hours by discipline during episode:\n- Skilled Nursing: XX visits, XX hours\n- PT: XX visits\n- OT: XX visits\n- ST: XX visits" }
        ]
      });
    }

    // ========== DIAGNOSIS AND CODING (initial and recert only) ==========
    categories.push({
      name: "Diagnosis and Coding",
      priority: 1,
      required: true,
      fields: [
        { name: "Primary ICD-10 Code", type: "select", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[] },
        { name: "Secondary ICD-10 Codes", type: "custom-icd10-list" as any,
          showFor: ['initial', 'recertification'] as AssessmentType[] },
        { name: "Medical Necessity Justification", type: "textarea", required: assessmentType !== 'discharge',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Document why skilled nursing services are medically necessary. Include: clinical complexity, skilled procedures required, teaching needs, and expected outcomes." }
      ]
    });

    // ========== HOME HEALTH CERTIFICATION (initial and recert only) ==========
    if (formConfig.settingType === 'home-health' && assessmentType !== 'discharge') {
      categories.push({
        name: "Home Health Certification",
        priority: 2,
        required: true,
        description: assessmentType === 'recertification' 
          ? "Recertification of homebound status and continued service need."
          : "Homebound status and service recommendations.",
        fields: [
          { name: "Homebound Status", type: "select", required: true,
            options: ["Patient is homebound due to medical condition", "Patient requires considerable effort to leave home", 
                      "Patient leaves home for medical appointments only", "Patient is bedbound or requires assistance to ambulate"] },
          { name: "Homebound Status Justification", type: "textarea", required: true,
            placeholder: "Document specific reasons patient is homebound" },
          { name: "Referring Physician", type: "text", required: assessmentType === 'initial',
            showFor: ['initial'] as AssessmentType[],
            placeholder: "Physician name who referred/ordered home health services" },
          { name: "Referring Physician NPI", type: "text",
            showFor: ['initial'] as AssessmentType[],
            placeholder: "10-digit NPI number" },
          { name: "Verbal Orders Obtained", type: "select", required: assessmentType === 'initial',
            showFor: ['initial'] as AssessmentType[],
            options: ["Yes - verbal orders obtained, pending written signature", "No - written orders on file from referral", "Pending - awaiting physician response"] },
          { name: "Verbal Order Date", type: "date", showFor: ['initial'] as AssessmentType[] },
          { name: "Verbal Order Received By", type: "text",
            showFor: ['initial'] as AssessmentType[],
            placeholder: "RN name who received verbal order (e.g., Jennifer Martinez, RN)" },
          { name: "Start of Care Date", type: "date", required: true, showFor: ['initial'] as AssessmentType[] },
          { name: "Recommended Skilled Nursing Frequency", type: "text", required: true, placeholder: "e.g., 16 hours/day PDN, 7 days/week" },
          { name: "Frequency Justification", type: "textarea", required: true, placeholder: "Explain why this frequency is medically necessary" },
          { name: "Anticipated Certification Period", type: "select", required: true,
            options: [medicareEnabled ? "60 days (standard Medicare)" : "60 days", "30 days", "90 days", "Other - specify in notes"] }
        ]
      });
    }

    // ========== PEDIATRIC INFORMATION (pediatric patients, all types but abbreviated for discharge) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Pediatric Information",
        priority: 3,
        required: true,
        description: assessmentType === 'recertification' ? "Update pediatric information. One-time fields are optional." 
                   : assessmentType === 'discharge' ? "Final pediatric status at discharge." : undefined,
        fields: [
          { name: "Chronological Age", type: "text", required: true, placeholder: "e.g., 2 years 4 months" },
          { name: "Gestational Age at Birth", type: "text", required: false, showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 32 weeks" },
          { name: "Birth Weight", type: "text", required: false, showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 1250 grams" },
          { name: "Birth History", type: "textarea", required: false, showFor: ['initial'] as AssessmentType[], placeholder: "Include NICU stay, birth complications, diagnoses at birth" },
          { name: "Primary Caregiver", type: "text", required: true, 
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Name and relationship to patient" },
          { name: "Secondary Caregiver", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[], placeholder: "Name and relationship (if applicable)" },
          { name: "Developmental Status", type: "select", required: true,
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Appropriate for age", "Mild delay", "Moderate delay", "Severe delay", "Profound delay"] },
          { name: "Developmental Milestones", type: "textarea",
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Document current developmental milestones achieved" },
          { name: "Feeding Method", type: "select", required: true,
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Oral feeding - age appropriate", "Breastfeeding", "Bottle feeding", "G-tube feeding", "J-tube feeding", "NG tube feeding", "Combination oral and tube feeding", "TPN"] },
          { name: "School/Daycare Attendance", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Not applicable (too young)", "Attending school/daycare", "Homebound due to medical condition", "Receiving special education services (IEP)", "Receiving early intervention services", "Other"] },
          { name: "Immunization Status", type: "select", required: assessmentType === 'initial',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Up to date", "Behind schedule - catching up", "Behind schedule - medical contraindication", "Modified schedule per physician", "Parent refusal", "Unknown"] },
          { name: "Growth Parameters", type: "textarea",
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Current weight, height/length, head circumference with percentiles" },
          { name: "Pediatric Pain Scale Used", type: "select",
            options: ["FLACC (0-3 years or non-verbal)", "Wong-Baker FACES (3+ years)", "Numeric Scale (8+ years)", "CRIES (neonates)", "N/A - unable to assess"] }
        ]
      });

      // ========== PEDIATRIC PDN SPECIALIZED CARE (pediatric + PDN, all types but different for discharge) ==========
      if (formConfig.nursingLevel === 'private-duty') {
        categories.push({
          name: "Pediatric PDN Specialized Care",
          priority: 3.5,
          required: true,
          description: assessmentType === 'discharge' 
            ? "Final status of specialized care needs at discharge."
            : "Required for Texas Medicaid PDN authorization (Modifier UA).",
          fields: [
            // Tracheostomy Section
            { name: "Tracheostomy Status", type: "select", required: true,
              options: assessmentType === 'discharge' 
                ? ["No tracheostomy", "Tracheostomy present at discharge", "Tracheostomy decannulated during episode"]
                : ["No tracheostomy", "Tracheostomy present - see details below"] },
            { name: "Tracheostomy Type and Size", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[], placeholder: "e.g., Shiley Pediatric 4.0 cuffed" },
            { name: "Tracheostomy Cuff Status", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no trach", "Cuffed - inflated", "Cuffed - deflated", "Uncuffed"] },
            { name: "Last Tracheostomy Change Date", type: "date", showFor: ['initial', 'recertification'] as AssessmentType[] },
            { name: "Tracheostomy Change Frequency", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no trach", "Weekly", "Every 2 weeks", "Monthly", "As needed", "Per physician order"] },
            { name: "Suctioning Frequency", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A", "PRN only", "Every 2 hours", "Every 4 hours", "Every shift", "Continuous/frequent (>6x per shift)"] },
            { name: "Secretion Characteristics", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A", "Thin, clear - minimal", "Thin, clear - moderate", "Thick, white/yellow", "Thick, green/brown", "Blood-tinged"] },
            
            // Ventilator Section
            { name: "Ventilator Dependency", type: "select", required: true,
              options: assessmentType === 'discharge'
                ? ["No ventilator", "Ventilator dependent at discharge", "Weaned from ventilator during episode"]
                : ["No ventilator", "Ventilator dependent - continuous (24 hours)", "Ventilator dependent - nocturnal only", "Ventilator dependent - nap and nocturnal", "Weaning in progress"] },
            { name: "Ventilator Type/Model", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[], placeholder: "e.g., Trilogy 100, LTV 1200" },
            { name: "Ventilator Mode", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no ventilator", "AC/VC (Assist Control/Volume Control)", "AC/PC (Assist Control/Pressure Control)", "SIMV/VC", "SIMV/PC", "SIMV/PS", "PSV (Pressure Support)", "CPAP", "BiPAP/BiLevel"] },
            { name: "Ventilator Settings", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Rate: ___ bpm\nPEEP: ___ cmH2O\nFiO2: ___%\nTidal Volume: ___ mL" },
            { name: "Oxygen Requirements", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["Room air only", "Oxygen via ventilator - see FiO2 above", "Supplemental O2 via nasal cannula", "Supplemental O2 via trach collar", "Blended O2 - specify below"] },
            { name: "Oxygen Liter Flow/FiO2", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[], placeholder: "e.g., 0.5 LPM, 30% FiO2" },
            
            // Emergency Equipment (initial and recert only)
            { name: "Emergency Equipment Verification", type: "select", required: assessmentType === 'initial',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["All equipment present and functional", "Missing items - documented in notes", "Equipment malfunction - documented in notes"] },
            { name: "Backup Tracheostomy - Same Size", type: "select", required: assessmentType === 'initial',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no trach", "Present at bedside", "Present in home - not at bedside", "Not present - ordered", "Not present - needs order"] },
            { name: "Backup Tracheostomy - One Size Smaller", type: "select", required: assessmentType === 'initial',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no trach", "Present at bedside", "Present in home - not at bedside", "Not present - ordered", "Not present - needs order"] },
            { name: "Manual Resuscitator (Ambu Bag)", type: "select", required: assessmentType === 'initial',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["Present and functional", "Present - needs replacement", "Not present - ordered", "Not present - needs order"] },
            { name: "Suction Machine - Portable", type: "select", required: assessmentType === 'initial',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["Present and functional", "Present - needs service", "Not present - ordered", "Not present - needs order", "N/A - no suctioning required"] },
            { name: "Backup Suction Machine", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["Present and functional", "Not present - recommended", "N/A"] },
            { name: "Pulse Oximeter", type: "select", required: assessmentType === 'initial',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["Present and functional with pediatric probe", "Present - needs new probe/batteries", "Not present - ordered", "Not present - needs order"] },
            { name: "Oxygen Tank - Portable", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - room air only", "Present and full", "Present - needs refill", "Not present - ordered", "Not present - needs order"] },
            { name: "Emergency Equipment Notes", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Document any missing equipment or equipment issues" },
            
            // Seizure Management
            { name: "Seizure History", type: "select", required: true,
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["No seizure history", "History of seizures - currently controlled", "History of seizures - breakthrough seizures occur", "Active seizure disorder - frequent seizures", "Status epilepticus risk"] },
            { name: "Seizure Type(s)", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Describe seizure types observed (tonic-clonic, absence, focal, etc.)" },
            { name: "Seizure Frequency", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no seizures", "Rare (less than monthly)", "Monthly", "Weekly", "Daily", "Multiple daily"] },
            { name: "Rescue Medication Available", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no seizure history", "Diastat (rectal diazepam) - present", "Midazolam nasal spray - present", "Midazolam buccal - present", "Other rescue med - present", "Prescribed but not present", "Not prescribed"] },
            { name: "Seizure Protocol on File", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["N/A - no seizure history", "Yes - reviewed and current", "Yes - needs update", "No - needs to be created", "No - physician order needed"] },
            
            // Responsible Adult (initial and recert only)
            { name: "Responsible Adult Resides in Home", type: "select", required: assessmentType !== 'discharge',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["Yes - trained in all emergency procedures", "Yes - partially trained, needs additional training", "Yes - not trained, training scheduled", "No - alternative arrangement in place", "No - barrier to services"] },
            { name: "Responsible Adult Name and Relationship", type: "text", required: assessmentType !== 'discharge',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "e.g., Maria Garcia - Mother" },
            { name: "Responsible Adult Emergency Skills Verified", type: "textarea",
              showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Document caregiver competency in: CPR, trach replacement, suctioning, ambu bag use, vent troubleshooting, seizure response" },
            { name: "Contingency Plan - Nurse Unavailable", type: "textarea", required: assessmentType === 'initial',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Document plan when scheduled nurse is unavailable" },
            { name: "Power Outage Plan", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Document plan for power outages: battery backup, generator, evacuation plan" },
            { name: "Emergency Contact - After Hours", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Name and phone number for after-hours emergencies" },
            
            // Texas Medicaid Specific (initial and recert only)
            { name: "Qualifies for Modifier UA (Specialized PDN)", type: "select", required: assessmentType !== 'discharge',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              options: ["Yes - tracheostomy with mechanical ventilation", "Yes - tracheostomy requiring frequent suctioning", "No - standard PDN services"] },
            { name: "PDN Hours Requested Per Day", type: "text", required: assessmentType !== 'discharge',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "e.g., 16 hours/day" },
            { name: "PDN Hours Justification", type: "textarea", required: assessmentType !== 'discharge',
              showFor: ['initial', 'recertification'] as AssessmentType[],
              placeholder: "Justify the requested hours based on: medical complexity, caregiver capability, safety requirements" }
          ]
        });
      }
    }


    // ========== NURSING ASSESSMENT (all types) ==========
    categories.push({
      name: "Nursing Assessment",
      priority: 4,
      required: true,
      fields: [
        { name: "Chief Complaint", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Primary reason for nursing services" },
        { name: "History of Present Illness", type: "textarea", required: assessmentType === 'initial',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Current medical condition and recent changes" },
        { name: "Past Medical History", type: "textarea", showFor: ['initial'] as AssessmentType[], placeholder: "Significant past medical conditions" },
        { name: "Surgical History", type: "textarea", showFor: ['initial'] as AssessmentType[], placeholder: "List all surgeries with approximate dates" },
        { name: "Medication Review Completed", type: "select", required: true,
          options: ["Reviewed medications module - current and accurate", "Reviewed medications module - updates needed", "Medications module needs reconciliation"] },
        { name: "Medications Started Since Last Assessment", type: "textarea",
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "New meds with start date and reason (e.g., Started Omeprazole 10mg PO daily on 02/20/25 for reflux)" },
        { name: "Medications Discontinued Since Last Assessment", type: "textarea",
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Stopped meds with date and reason (e.g., Discontinued Prevacid 02/18/25 - ineffective)" },
        { name: "Medication Dose Changes", type: "textarea",
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Dose changes with date and reason (e.g., Keppra increased from 250mg to 500mg BID on 02/15/25 - breakthrough seizures)" },
        { name: "Medication Adherence/Concerns", type: "textarea",
          placeholder: "Adherence status, side effects observed, administration difficulties, caregiver concerns" },
        { name: "Allergies", type: "textarea", required: true, placeholder: "Drug allergies, food allergies, environmental allergies (include reaction type)" },
        { name: "Pain Assessment", type: "select", required: true,
          options: ["0/10 (no pain)", "1-2/10 (mild)", "3-4/10 (moderate)", "5-6/10 (moderate-severe)", "7-8/10 (severe)", "9-10/10 (worst possible)", "Unable to assess"] },
        { name: "Pain Location and Character", type: "textarea", placeholder: "If pain present, describe location, quality, duration" }
      ]
    });

    // ========== PHYSICAL ASSESSMENT (all types) ==========
    categories.push({
      name: "Physical Assessment",
      priority: 5,
      required: true,
      fields: [
        { name: "General Appearance", type: "select", required: true,
          options: ["Appears well, no acute distress", "Appears ill", "Appears stated age", "Appears older than stated age", "In acute distress", "Lethargic", "Alert and oriented"] },
        { name: "Vital Signs", type: "textarea", required: true,
          placeholder: "Temperature: ___F\nHeart Rate: ___ bpm\nBlood Pressure: ___/___\nRespiratory Rate: ___\nOxygen Saturation: ___%\nWeight: ___ kg/lbs" },
        { name: "Respiratory Status", type: "select", required: true,
          options: ["Respirations even and unlabored", "Tachypneic", "Bradypneic", "Dyspneic", "Using accessory muscles", "On oxygen therapy", "Mechanical ventilation"] },
        { name: "Breath Sounds", type: "select",
          options: ["Clear bilaterally", "Diminished", "Wheezes", "Crackles/Rales", "Rhonchi", "Stridor", "Absent"] },
        { name: "Cardiovascular Status", type: "select", required: true,
          options: ["Regular rate and rhythm", "Irregular rhythm", "Tachycardic", "Bradycardic", "Murmur present", "Peripheral edema", "Good peripheral pulses"] },
        { name: "Neurological Status", type: "select", required: true,
          options: ["Alert and oriented x3", "Confused", "Lethargic", "Responds to voice", "Responds to pain only", "Unresponsive", "Focal deficits present"] },
        { name: "Skin Integrity", type: "select", required: true,
          options: ["Intact, no concerns", "Pressure ulcer present", "Surgical wound present", "Rash present", "Bruising present", "Poor healing", "Signs of infection"] },
        { name: "Skin Assessment Details", type: "textarea", placeholder: "If skin issues present, document location, size, stage, appearance, treatment" }
      ]
    });

    // ========== MOBILITY ASSESSMENT (all types) ==========
    categories.push({
      name: "Mobility Assessment",
      priority: 5.4,
      required: true,
      description: "Age-appropriate mobility evaluation. For infants: developmental motor level. For older children: functional mobility.",
      fields: [
        { name: "Mobility Status", type: "select", required: true,
          options: [
            "Non-ambulatory - bedbound",
            "Non-ambulatory - sits with support",
            "Non-ambulatory - sits independently",
            "Wheelchair - power",
            "Wheelchair - manual with assistance",
            "Wheelchair - manual independent",
            "Ambulatory - walker",
            "Ambulatory - crutches",
            "Ambulatory - cane",
            "Ambulatory - gait trainer",
            "Ambulatory - independent",
            "Age-appropriate crawling (infant)",
            "Age-appropriate cruising (toddler)",
            "Age-appropriate walking (toddler)"
          ] },
        { name: "Transfer Ability", type: "select", required: true,
          options: [
            "N/A - infant (carried)",
            "Total assistance required (dependent lift)",
            "Maximum assistance (>75% help)",
            "Moderate assistance (50-75% help)",
            "Minimal assistance (25-50% help)",
            "Supervision only",
            "Independent transfers"
          ] },
        { name: "Assistive Devices Used", type: "text",
          placeholder: "e.g., Manual wheelchair, gait trainer, stander, walker, AFO braces" },
        { name: "Positioning Needs", type: "textarea",
          placeholder: "For non-ambulatory: positioning schedule, equipment, handling precautions (e.g., Supine/prone/side-lying q2h, uses tumble form seat, requires head support)" },
        { name: "Mobility Notes", type: "textarea",
          placeholder: "Endurance, balance, special considerations, weight-bearing status" }
      ]
    });

    // ========== FUNCTIONAL LIMITATIONS (all types) ==========
    categories.push({
      name: "Functional Limitations",
      priority: 5.5,
      required: false,
      fields: [
        { name: "Amputation", type: "checkbox" },
        { name: "Bowel/Bladder Incontinence", type: "checkbox" },
        { name: "Contracture", type: "checkbox" },
        { name: "Hearing Impairment", type: "checkbox" },
        { name: "Paralysis", type: "checkbox" },
        { name: "Endurance", type: "checkbox" },
        { name: "Ambulation", type: "checkbox" },
        { name: "Speech", type: "checkbox" },
        { name: "Legally Blind", type: "checkbox" },
        { name: "Dyspnea", type: "checkbox" },
        { name: "Other Functional Limitation", type: "checkbox" },
        { name: "Other Functional Limitation Details", type: "textarea", placeholder: "If 'Other' is checked, describe the functional limitation" }
      ]
    });

    // ========== ACTIVITIES OF DAILY LIVING (pediatric only) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Activities of Daily Living (Age-Appropriate)",
        priority: 5.6,
        required: true,
        description: "For infants/toddlers (0-3): Mark 'Dependent - age appropriate'. For ages 4-18: Assess actual independence level.",
        fields: [
          { name: "Feeding Independence", type: "select", required: true,
            options: [
              "Dependent - age appropriate (0-3 years)",
              "Dependent - delayed for age",
              "Partial assistance required",
              "Modified independent (adaptive equipment)",
              "Independent for age"
            ] },
          { name: "Bathing Independence", type: "select", required: true,
            options: [
              "Dependent - age appropriate (0-5 years)",
              "Dependent - delayed for age",
              "Partial assistance required",
              "Modified independent (supervision only)",
              "Independent for age"
            ] },
          { name: "Dressing Independence", type: "select", required: true,
            options: [
              "Dependent - age appropriate (0-4 years)",
              "Dependent - delayed for age",
              "Partial assistance required",
              "Modified independent (simple clothing)",
              "Independent for age"
            ] },
          { name: "Toileting/Continence", type: "select", required: true,
            options: [
              "Diapers - age appropriate (0-3 years)",
              "Diapers - not age appropriate (4+ years)",
              "Toilet trained - daytime only",
              "Toilet trained - day and night",
              "Partial incontinence",
              "Total incontinence",
              "Catheter - Foley",
              "Catheter - intermittent",
              "Ostomy present"
            ] },
          { name: "ADL Notes", type: "textarea",
            placeholder: "Additional ADL information, adaptive equipment, special considerations" }
        ]
      });
    }

    // ========== COMMUNICATION ASSESSMENT (all types) ==========
    categories.push({
      name: "Communication Assessment",
      priority: 5.7,
      required: true,
      description: "Speech and language evaluation. Critical for all pediatric patients.",
      fields: [
        { name: "Communication Ability", type: "select", required: true,
          options: [
            "Pre-verbal - age appropriate (0-12 months)",
            "Pre-verbal - delayed for age",
            "Non-verbal - uses AAC device",
            "Non-verbal - no AAC",
            "Limited verbal - single words",
            "Limited verbal - 2-3 word phrases",
            "Age-appropriate speech",
            "Fluent speech for age"
          ] },
        { name: "Expressive Language", type: "select", required: true,
          options: [
            "N/A - pre-verbal infant",
            "Severely delayed",
            "Moderately delayed",
            "Mildly delayed",
            "Age-appropriate",
            "Advanced for age"
          ] },
        { name: "Receptive Language", type: "select", required: true,
          options: [
            "N/A - too young",
            "Severely impaired",
            "Moderately impaired",
            "Mildly impaired",
            "Age-appropriate",
            "Advanced for age"
          ] },
        { name: "AAC Device", type: "select",
          options: [
            "None - not needed",
            "None - recommended",
            "Picture board/PECS",
            "Speech generating device",
            "Eye gaze system",
            "Sign language",
            "Other"
          ] },
        { name: "Speech Intelligibility", type: "select",
          options: [
            "N/A - non-verbal",
            "Unintelligible",
            "Partially intelligible - context needed",
            "Mostly intelligible",
            "Fully intelligible"
          ] },
        { name: "Communication Notes", type: "textarea",
          placeholder: "How patient communicates needs, pain, preferences. Include hearing/vision status if impacts communication." }
      ]
    });

    // ========== NEUROLOGICAL & COGNITIVE ASSESSMENT ==========
    categories.push({
      name: "Neurological & Cognitive Assessment",
      priority: 6,
      required: false,
      fields: [
        // Orientation & Mental Status (Adult)
        { name: "Orientation - Person", type: "checkbox", group: "Orientation" },
        { name: "Orientation - Place", type: "checkbox", group: "Orientation" },
        { name: "Orientation - Time", type: "checkbox", group: "Orientation" },
        { name: "Orientation - Situation", type: "checkbox", group: "Orientation" },
        { name: "Alert & Oriented", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Confused", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Forgetful", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Disoriented", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Depressed", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Agitated", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Anxious", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Lethargic", type: "checkbox", group: "Mental Status Indicators" },
        { name: "Comatose", type: "checkbox", group: "Mental Status Indicators" },

        // Pediatric Cognitive & Developmental Status
        { name: "Level of Consciousness", type: "select", required: false, options: [
          "Alert & Responsive",
          "Lethargic",
          "Obtunded",
          "Stuporous",
          "Comatose"
        ] },
        { name: "Developmental Age - Gross Motor", type: "text", placeholder: "e.g., 18-24 month level" },
        { name: "Developmental Age - Fine Motor", type: "text", placeholder: "e.g., 18-24 month level" },
        { name: "Developmental Age - Cognitive", type: "text", placeholder: "e.g., 18-24 month level" },
        { name: "Developmental Age - Language", type: "text", placeholder: "e.g., 18-24 month level" },

        { name: "Recognizes Familiar Caregivers", type: "checkbox", group: "Cognitive/Behavioral Status" },
        { name: "Responds to Name", type: "checkbox", group: "Cognitive/Behavioral Status" },
        { name: "Follows Simple Commands", type: "checkbox", group: "Cognitive/Behavioral Status" },
        { name: "Makes Eye Contact", type: "checkbox", group: "Cognitive/Behavioral Status" },
        { name: "Interacts Socially", type: "checkbox", group: "Cognitive/Behavioral Status" },

        { name: "Self-Injurious Behavior", type: "checkbox", group: "Behavioral Concerns" },
        { name: "Aggressive Behavior", type: "checkbox", group: "Behavioral Concerns" },
        { name: "Inconsolable Crying", type: "checkbox", group: "Behavioral Concerns" },
        { name: "Sensory Sensitivities", type: "checkbox", group: "Behavioral Concerns" },

        { name: "Mental Status & Developmental Summary", type: "textarea", placeholder: "Describe patient's cognitive function, responsiveness, developmental level, and behavioral patterns...", rows: 4 }
      ]
    });

    // ========== DURABLE MEDICAL EQUIPMENTS (DME) (initial and recert only) ==========
    if ((formConfig.settingType === 'home-health' || formConfig.nursingLevel === 'skilled' || formConfig.nursingLevel === 'private-duty') && assessmentType !== 'discharge') {
      categories.push({
        name: "Durable Medical Equipments (DME)",
        priority: 6,
        required: false,
        fields: [
          {
            name: "Durable Medical Equipment and Supplies (CMS-485 Section 7)",
            type: "textarea",
            required: true,
            showFor: ['initial', 'recertification'] as AssessmentType[],
            rows: 10,
            placeholder: `List all equipment with details. Example format:

Ventilator: [Model] - Settings: [Mode, Rate, PEEP, FiO2, etc.]
Tracheostomy: [Type, Size, Cuff status] - Changed [frequency]
Suction Machine: [Model] with backup unit
Oxygen: [Type - concentrator/tank] providing [delivery method]
Feeding Pump: [Model] with pole
Pulse Oximeter: Continuous monitoring with alarm capability
Emergency Equipment: Backup trach (same size and one size smaller), Ambu bag (size), portable suction, backup ventilator battery

Supplies: Suction catheters (sizes: 8Fr, 10Fr), trach ties, trach care supplies, feeding bags/extensions, ventilator circuits (changed weekly)

Equipment Supplier: [Company Name and Phone]`
          }
        ]
      });
    }

    // ========== HOME ENVIRONMENT ASSESSMENT (home health, initial and recert) ==========
    if (formConfig.settingType === 'home-health' && assessmentType !== 'discharge') {
      categories.push({
        name: "Home Environment Assessment",
        priority: 7,
        required: assessmentType === 'initial',
        description: assessmentType === 'recertification' ? "Document any changes to home environment since last assessment." : undefined,
        fields: [
          { name: "Home Safety", type: "select", required: assessmentType === 'initial',
            options: ["Safe environment", "Minor safety concerns - addressed", "Minor safety concerns - pending", "Major safety concerns - intervention needed", "Unsafe environment"] },
          { name: "Home Safety Details", type: "textarea", placeholder: "Document specific safety concerns or interventions" },
          { name: "Cleanliness", type: "select", required: assessmentType === 'initial',
            options: ["Clean and well-maintained", "Somewhat cluttered but sanitary", "Unsanitary conditions", "Hoarding concerns", "Pest infestation"] },
          { name: "Accessibility", type: "select",
            options: ["Wheelchair accessible", "Modifications needed", "Stairs present - patient can navigate", "Stairs present - barrier to care", "Ramps available", "Grab bars installed", "Stair lift present"] },
          { name: "Utilities", type: "select", required: assessmentType === 'initial',
            options: ["All utilities functioning", "Heating/cooling concerns", "Plumbing issues", "Electrical concerns", "No running water", "No electricity"] },
          { name: "Medication Storage", type: "select",
            options: ["Stored safely and organized", "Stored safely but disorganized", "Potential access by children - addressed", "Potential access by children - concern", "Expired medications present", "Refrigerated meds stored properly", "Storage temperature concerns"] },
          { name: "Oxygen Safety", type: "select",
            options: ["N/A - no oxygen in home", "All precautions in place", "No smoking signs posted", "Oxygen stored properly", "Safety concerns identified - see notes"] },
          { name: "Emergency Access", type: "select",
            options: ["Easy emergency access to home", "Lockbox present with code", "Key available to EMS", "Access may be difficult - plan in place", "Access concerns - needs intervention"] },
          { name: "Environment Notes", type: "textarea", placeholder: "Additional notes about home environment, pets, smoking in home, etc." }
        ]
      });
    }

    // ========== CAREGIVER ASSESSMENT (initial and recert only) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Caregiver Assessment",
        priority: 8,
        required: true,
        description: assessmentType === 'recertification' ? "Update caregiver status and training progress." : undefined,
        fields: [
        { name: "Primary Caregiver", type: "text", required: true, placeholder: "Name and relationship to patient" },
        { name: "Primary Caregiver Contact", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[], placeholder: "Phone number" },
        { name: "Caregiver Knowledge Level", type: "select", required: true,
          options: ["Excellent understanding of condition and care", "Good understanding", "Moderate understanding - education ongoing", "Limited understanding - significant education needed", "No understanding - unable to learn"] },
        { name: "Caregiver Competency", type: "select", required: true,
          options: ["Demonstrates all skills independently", "Needs occasional verbal reminders", "Needs frequent verbal cues", "Needs hands-on assistance", "Unable to perform tasks safely"] },
        { name: "Caregiver Availability", type: "select", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["24/7 availability", "Daytime availability only", "Evening/night availability only", "Limited availability - specify in notes", "Weekend availability only", "Emergency contact only"] },
        { name: "Caregiver Burden", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Well-supported, minimal burden", "Moderate stress levels - coping well", "High stress levels - needs support", "Caregiver burnout concerns", "Multiple caregivers sharing responsibility", "Respite care needed"] },
        { name: "Education Provided", type: "textarea", placeholder: "Specific education and training provided to caregiver" },
        { name: "Return Demonstration", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Successful return demonstration", "Partial return demonstration - needs practice", "Unable to demonstrate - reteach next visit", "Needs additional training sessions", "Not applicable"] },
        { name: "Caregiver Teaching Needs", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "List topics requiring additional education" }
      ]
    });
  }

    // ========== ASSESSMENT ATTESTATION (all types) ==========
    categories.push({
      name: "Assessment Attestation",
      priority: 9,
      required: true,
      description: assessmentType === 'discharge' ? "Final attestation for discharge assessment." 
                 : assessmentType === 'recertification' ? "Attestation for recertification assessment."
                 : "Complete assessment summary. Care plan is documented separately.",
      fields: [
        { name: "Assessment Summary", type: "textarea", placeholder: "Brief summary of key clinical findings" },
        { name: "Recommended Nursing Interventions", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Suggested interventions to be included in the care plan" },
        { name: "Nursing Interventions Provided", type: "textarea", showFor: ['discharge'] as AssessmentType[],
          placeholder: "Summary of nursing interventions provided during episode: skilled assessments, medication management, education, procedures performed" },
        { name: "Priority Problems Identified", type: "textarea", placeholder: "List priority nursing problems/diagnoses identified" },
        { name: "Referrals Recommended", type: "textarea", placeholder: "Any referrals to other disciplines or services recommended" },
        { name: "RN Signature Attestation", type: "select", required: true,
          options: assessmentType === 'discharge'
            ? ["I attest this discharge assessment is complete and accurate", "Assessment pending additional information"]
            : ["I attest this assessment is complete and accurate", "Assessment pending additional information"] }
      ]
    });

    // ========== OPTIONAL ADVANCED ASSESSMENTS ==========
    if (formConfig.showOptionalSections) {
      categories.push({
        name: "Nutritional Assessment",
        priority: 10,
        required: false,
        fields: [
          { name: "Nutritional Status", type: "select", options: ["Well-nourished", "At risk for malnutrition", "Malnourished", "Obese", "Failure to thrive"] },
          { name: "Feeding Difficulties", type: "select", options: ["No difficulties", "Swallowing problems", "Chewing difficulties", "Feeding intolerance", "Requires assistance"] },
          { name: "Special Diet", type: "textarea", placeholder: "Specific dietary requirements or restrictions" },
          { name: "Hydration Status", type: "select", options: ["Well-hydrated", "Mild dehydration", "Moderate dehydration", "Severe dehydration", "Fluid overload"] }
        ]
      });

      categories.push({
        name: "Psychosocial Assessment",
        priority: 11,
        required: false,
        fields: [
          { name: "Mood/Affect", type: "select", options: ["Appropriate mood", "Anxious", "Depressed", "Agitated", "Withdrawn", "Confused"] },
          { name: "Coping Mechanisms", type: "select", options: ["Effective coping", "Some difficulty coping", "Poor coping skills", "Maladaptive coping", "Seeking support"] },
          { name: "Social Support", type: "select", options: ["Strong support system", "Adequate support", "Limited support", "No support system", "Isolated"] },
          { name: "Financial Concerns", type: "select", options: ["No financial concerns", "Some financial stress", "Significant financial burden", "Unable to afford care", "Insurance issues"] }
        ]
      });

      categories.push({
        name: "Advanced Assessment",
        priority: 12,
        required: false,
        fields: [
          { name: "Elimination", type: "select", options: ["Continent", "Occasional incontinence", "Frequent incontinence", "Incontinent", "Catheter in place", "Ostomy present"] },
          { name: "Sleep Patterns", type: "select", options: ["Sleeps well", "Occasional insomnia", "Frequent sleep disturbances", "Severe sleep problems", "Requires sleep aids"] }
        ]
      });
    }

    return categories.sort((a, b) => a.priority - b.priority);
  };

  // Form data state management
  const [formData, setFormData] = useState(() => {
    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      // Load existing secondary ICD-10 codes if they exist
      const existingSecondaryICD10 = existingData.categories?.['Diagnosis and Coding-Secondary ICD-10 Codes'];
      if (existingSecondaryICD10 && typeof existingSecondaryICD10 === 'string') {
        const codes = splitIcd10Field(existingSecondaryICD10);
        setSecondaryICD10Codes(codes);
      }
      return existingData;
    }

    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        categories[`${category.name}-${field.name}`] = "";
      });
    });
    return {
      id: "",
      date: new Date().toISOString().split('T')[0],
      provider: "Current User, RN",
      status: "Pending",
      category: "Nursing",
      lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }),
      categories: categories,
      formConfig: formConfig
    };
  });
  
  useEffect(() => {
    // Only run this effect once when existingData is first loaded
    // Skip if we've already loaded the initial data to prevent resetting user changes
    if (initialLoadCompleteRef.current) {
      return;
    }

    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      setFormData(existingData);
      // Merge existing formConfig with defaults to ensure all fields are present
      const mergedConfig = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      mergedConfig.assessmentType = normalizeAssessmentType(mergedConfig.assessmentType);
      setFormConfig(mergedConfig);
      // Mark initial load as complete after loading existing data
      initialLoadCompleteRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultFormConfig is a fresh object every render; only a new existingData should reload the form
  }, [existingData]);

  useEffect(() => {
    // Skip the first run when editing existing data - let the existingData useEffect handle it
    if (existingData && !initialLoadCompleteRef.current) {
      return;
    }

    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        const fieldKey = `${category.name}-${field.name}`;
        categories[fieldKey] = formData.categories?.[fieldKey] || "";
      });
    });

    setFormData(prev => ({
      ...prev,
      categories: { ...prev.categories, ...categories },
      formConfig: formConfig
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only when the form type fields change; adding formConfig/formData would rebuild the categories on every edit
  }, [formConfig.assessmentType, formConfig.patientType, formConfig.settingType, formConfig.nursingLevel, formConfig.showOptionalSections]);

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      categories: { ...(prev.categories || {}), [fieldKey]: value }
    }));
  };

  const handleAddSecondaryICD10 = () => {
    if (selectedSecondaryICD10 && !secondaryICD10Codes.includes(selectedSecondaryICD10)) {
      const updatedCodes = [...secondaryICD10Codes, selectedSecondaryICD10];
      setSecondaryICD10Codes(updatedCodes);
      handleInputChange('Diagnosis and Coding-Secondary ICD-10 Codes', joinIcd10Field(updatedCodes));
      setSelectedSecondaryICD10('');
    }
  };

  const handleRemoveSecondaryICD10 = (codeToRemove: string) => {
    const updatedCodes = secondaryICD10Codes.filter(code => code !== codeToRemove);
    setSecondaryICD10Codes(updatedCodes);
    handleInputChange('Diagnosis and Coding-Secondary ICD-10 Codes', joinIcd10Field(updatedCodes));
  };

  const handleConfigChange = (key, value) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const toggleAllSections = (collapse: boolean) => {
    // Use all current category names for dynamic expand/collapse
    const allCategoryNames = getAssessmentCategories().map(cat => cat.name);
    const newState: any = {};
    allCategoryNames.forEach(key => {
      newState[key] = collapse;
    });
    setCollapsedSections(newState);
    setConfigSectionCollapsed(collapse);
  };

  useEffect(() => {
    const handleToggleAll = (event: any) => {
      toggleAllSections(event.detail.collapse);
    };
    window.addEventListener('toggleAllSections', handleToggleAll);
    return () => window.removeEventListener('toggleAllSections', handleToggleAll);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toggleAllSections is recreated every render; the listener is re-registered when collapsedSections changes
  }, [collapsedSections]);

  const getCategoryIcon = (categoryName) => {
    const icons = {
      "Recertification Summary": <RefreshCw size={18} className="mr-2 text-amber-600" />,
      "Discharge Summary": <LogOut size={18} className="mr-2 text-green-600" />,
      "Diagnosis and Coding": <FileBarChart size={18} className="mr-2 text-blue-600" />,
      "Home Health Certification": <Shield size={18} className="mr-2 text-green-600" />,
      "Pediatric Information": <Baby size={18} className="mr-2 text-purple-600" />,
      "Pediatric PDN Specialized Care": <Heart size={18} className="mr-2 text-red-600" />,
      "Nursing Assessment": <Stethoscope size={18} className="mr-2 text-red-600" />,
      "Physical Assessment": <Activity size={18} className="mr-2 text-blue-700" />,
      "Mobility Assessment": <Activity size={18} className="mr-2 text-indigo-600" />,
      "Activities of Daily Living (Age-Appropriate)": <User size={18} className="mr-2 text-teal-600" />,
      "Communication Assessment": <FileText size={18} className="mr-2 text-purple-600" />,
      "Functional Limitations": <AlertTriangle size={18} className="mr-2 text-orange-600" />,
      "Neurological & Cognitive Assessment": <Brain size={18} className="mr-2 text-blue-700" />,
      "Durable Medical Equipments (DME)": <Thermometer size={18} className="mr-2 text-gray-700" />,
      "Home Environment Assessment": <Home size={18} className="mr-2 text-orange-600" />,
      "Caregiver Assessment": <Users size={18} className="mr-2 text-teal-600" />,
      "Assessment Attestation": <CheckCircle size={18} className="mr-2 text-green-600" />,
      "Nutritional Assessment": <Droplet size={18} className="mr-2 text-yellow-600" />,
      "Psychosocial Assessment": <Brain size={18} className="mr-2 text-pink-600" />,
      "Advanced Assessment": <Eye size={18} className="mr-2 text-gray-600" />
    };
    return icons[categoryName] || <FileText size={18} className="mr-2 text-gray-700" />;
  };

  const getCategoryColorClasses = (categoryName) => {
    const colorMap = {
      "Recertification Summary": { bg: "bg-amber-50", border: "border-amber-200", hover: "hover:bg-amber-100" },
      "Discharge Summary": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Diagnosis and Coding": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Home Health Certification": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Pediatric Information": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Pediatric PDN Specialized Care": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Nursing Assessment": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Physical Assessment": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Mobility Assessment": { bg: "bg-indigo-50", border: "border-indigo-200", hover: "hover:bg-indigo-100" },
      "Activities of Daily Living (Age-Appropriate)": { bg: "bg-teal-50", border: "border-teal-200", hover: "hover:bg-teal-100" },
      "Communication Assessment": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Functional Limitations": { bg: "bg-orange-50", border: "border-orange-200", hover: "hover:bg-orange-100" },
      "Neurological & Cognitive Assessment": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Durable Medical Equipments (DME)": { bg: "bg-gray-50", border: "border-gray-200", hover: "hover:bg-gray-100" },
      "Home Environment Assessment": { bg: "bg-orange-50", border: "border-orange-200", hover: "hover:bg-orange-100" },
      "Caregiver Assessment": { bg: "bg-teal-50", border: "border-teal-200", hover: "hover:bg-teal-100" },
      "Assessment Attestation": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Nutritional Assessment": { bg: "bg-yellow-50", border: "border-yellow-200", hover: "hover:bg-yellow-100" },
      "Psychosocial Assessment": { bg: "bg-pink-50", border: "border-pink-200", hover: "hover:bg-pink-100" },
      "Advanced Assessment": { bg: "bg-gray-50", border: "border-gray-200", hover: "hover:bg-gray-100" }
    };
    return colorMap[categoryName] || { bg: "bg-white", border: "border-gray-200", hover: "hover:bg-gray-50" };
  };

  const validateForm = () => {
    const errors = [];
    const categories = getAssessmentCategories();
    
    categories.forEach(section => {
      section.fields.forEach(field => {
        // Only validate visible fields
        if (shouldShowField(field) && field.required && !formData.categories?.[`${section.name}-${field.name}`]) {
          errors.push(`${field.name} is required`);
        }
      });
    });
    
    return errors;
  };

  // Payload passed to onSave (Save, Submit and autosave all use it)
  const buildSaveData = () => ({
    ...formData,
    formConfig: withPediatricFirstConfig(formConfig),
    status: 'Draft',
    lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })
  });

  // Save keeps a DRAFT and no longer blocks on required fields; Submit runs validate() (Phase 4)
  const handleSave = () => {
    onSave(buildSaveData());
  };


  useImperativeHandle(ref, () => ({
    triggerSave: () => handleSave(),
    validate: () => validateForm(),
    getSaveData: () => buildSaveData(),
    getSnapshot: () => JSON.stringify(formData),
  }));

  return (
    <div className="space-y-6">
      <div className="bg-white text-left">

        {/* Header */}
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggleAllSections(!Object.values(collapsedSections).every(v => v))}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors"
              >
                {Object.values(collapsedSections).every(v => v) ? (
                  <>
                    <ChevronsDown size={16} />
                    <span>Expand All</span>
                  </>
                ) : (
                  <>
                    <ChevronsUp size={16} />
                    <span>Collapse All</span>
                  </>
                )}
              </button>
          </div>
        </div>

        {/* Collapsible Configuration Section */}
        <div className="bg-gray-50 rounded-lg mb-6 border border-gray-200">
          <div 
            className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
            onClick={() => setConfigSectionCollapsed(!configSectionCollapsed)}
          >
            <div className="flex items-center gap-3">
              <FileEdit size={18} className="text-blue-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-700">Evaluation Configuration</h4>
                {configSectionCollapsed && formConfig.assessmentType && ASSESSMENT_TYPES[formConfig.assessmentType] && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {ASSESSMENT_TYPES[formConfig.assessmentType].label} • {patientTypeLabel(formConfig.patientType)}
                  </p>
                )}
              </div>
            </div>
            <div>
              {configSectionCollapsed ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
            </div>
          </div>

          <div className={configSectionCollapsed ? 'hidden' : 'p-4 pt-0'}>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Assessment Type</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {(Object.keys(ASSESSMENT_TYPES) as AssessmentType[]).map((type) => {
              const config = ASSESSMENT_TYPES[type];
              const isSelected = formConfig.assessmentType === type;
              const colorClasses = {
                blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                amber: isSelected ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                green: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-white'
              };
              const textColorClasses = {
                blue: isSelected ? 'text-blue-600' : 'text-gray-500',
                amber: isSelected ? 'text-amber-600' : 'text-gray-500',
                green: isSelected ? 'text-green-600' : 'text-gray-500'
              };
              
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => (!editingId || assessmentStatus === 'DRAFT') && !isReadOnly && handleConfigChange('assessmentType', type)}
                  disabled={(!!editingId && assessmentStatus !== 'DRAFT') || isReadOnly}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${colorClasses[config.color]} ${(editingId && assessmentStatus !== 'DRAFT') ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center mb-1">
                    <span className={`mr-2 ${textColorClasses[config.color]}`}>{config.icon}</span>
                    <span className={`font-medium text-sm ${isSelected ? `text-${config.color}-900` : 'text-gray-700'}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className={`text-xs ${isSelected ? `text-${config.color}-700` : 'text-gray-500'}`}>
                    {config.description}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Assessment Type Notice */}
          {formConfig.assessmentType === 'recertification' && (
            <div className="p-3 rounded-lg flex items-start mb-4 bg-amber-50 border border-amber-200">
              <RefreshCw size={18} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <strong>Recertification Mode:</strong> Birth history and one-time setup fields are hidden. 
                Focus on documenting changes since last assessment and justifying continued medical necessity.
              </div>
            </div>
          )}
          
          {formConfig.assessmentType === 'discharge' && (
            <div className="p-3 rounded-lg flex items-start mb-4 bg-green-50 border border-green-200">
              <LogOut size={18} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800">
                <strong>Discharge Mode:</strong> Service planning and authorization fields are hidden. 
                Focus on documenting outcomes, discharge instructions, and follow-up plans.
              </div>
            </div>
          )}

          {/* Form Configuration */}
          <h4 className="text-sm font-medium text-gray-700 mb-3 mt-4">Assessment Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <AdultTransitionToggle disabled={isReadOnly} patientType={formConfig.patientType}
              onChange={(patientType) => handleConfigChange('patientType', patientType)} />
            <div className="flex items-end">
              <label className="flex items-center text-sm text-gray-600">
                <input type="checkbox" disabled={isReadOnly} className="mr-2" checked={formConfig.showOptionalSections}
                  onChange={(e) => handleConfigChange('showOptionalSections', e.target.checked)} />
                Show Optional Sections
              </label>
            </div>
          </div>
          
          {/* PDN Notice */}
          {formConfig.patientType === 'pediatric' && formConfig.nursingLevel === 'private-duty' && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start">
              <Heart size={18} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <strong>Pediatric PDN Assessment:</strong> Includes specialized fields for Texas Medicaid PDN authorization (Modifier UA).
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Main form content - categories */}
        {getAssessmentCategories().map((category, categoryIndex) => {
          // Filter fields to only show visible ones for current assessment type
          const visibleFields = category.fields.filter(field => shouldShowField(field));

          // Skip rendering category if no visible fields
          if (visibleFields.length === 0) return null;

          // Special responsive layout for Functional Limitations checkboxes
          const isFunctionalLimitations = category.name === 'Functional Limitations';

          const colors = getCategoryColorClasses(category.name);
          
          return (
            <div key={categoryIndex} className="mb-6">
              <div className={`rounded-lg border ${colors.border} shadow-sm ${colors.bg}`}>
                <div className={`flex items-center justify-between cursor-pointer p-4 ${colors.hover} rounded-t-lg transition-colors`} onClick={() => toggleSection(category.name)}>
                  <div className="flex items-center">
                    {getCategoryIcon(category.name)}
                    <h4 className="text-base font-medium text-gray-900">{category.name}</h4>
                    {category.required && <span className="ml-2 text-xs text-red-600 font-medium">Required</span>}
                  </div>
                  <div>
                    {collapsedSections[category.name] ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
                  </div>
                </div>

                {category.description && !collapsedSections[category.name] && (
                  <p className="text-sm text-gray-600 px-4 pb-2 border-b border-gray-100">{category.description}</p>
                )}

                <div className={`p-4 ${collapsedSections[category.name] ? 'hidden' : 'block'}`}>
                {isFunctionalLimitations || category.name === 'Neurological & Cognitive Assessment' ? (
                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-2">
                      {visibleFields.filter(f => f.type === 'checkbox').map((field, fieldIndex) => {
                        const fieldKey = `${category.name}-${field.name}`;
                        return (
                          <label key={fieldKey} className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <input
                              type="checkbox"
                              id={fieldKey}
                              checked={formData.categories?.[fieldKey] === 'true' || formData.categories?.[fieldKey] === true}
                              onChange={(e) => handleInputChange(fieldKey, e.target.checked ? 'true' : 'false')}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            {field.name}
                          </label>
                        );
                      })}
                    </div>
                    {/* Only show the textarea if 'Other Functional Limitation' is checked */}
                    {isFunctionalLimitations && (() => {
                      const otherChecked = formData.categories?.[`${category.name}-Other Functional Limitation`] === 'true' || formData.categories?.[`${category.name}-Other Functional Limitation`] === true;
                      const otherField = visibleFields.find(f => f.name === 'Other Functional Limitation Details' && (f.type === 'textarea' || f.type === 'text'));
                      if (otherChecked && otherField) {
                        const fieldKey = `${category.name}-${otherField.name}`;
                        return (
                          <div key={fieldKey} className="col-span-full mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                              {otherField.name}
                              {otherField.required && <span className="ml-1 text-red-600">*</span>}
                            </label>
                            {otherField.type === 'textarea' ? (
                              <textarea rows={otherField.rows || 3}
                                disabled={isReadOnly}
                                className={`w-full p-2 border rounded text-base ${otherField.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                value={formData.categories?.[fieldKey] || ''}
                                onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                placeholder={getPlaceholder(otherField)} />
                            ) : (
                              <input type="text"
                                disabled={isReadOnly}
                                className={`w-full p-2 border rounded text-base ${otherField.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                value={formData.categories?.[fieldKey] || ''}
                                onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                placeholder={getPlaceholder(otherField)} />
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {/* Render all textarea/text fields for Neurological section (not just 'Other') */}
                    {category.name === 'Neurological & Cognitive Assessment' && visibleFields.filter(f => f.type === 'textarea' || f.type === 'text' || f.type === 'select').map((field, fieldIndex) => {
                      const fieldKey = `${category.name}-${field.name}`;
                      return (
                        <div key={fieldKey} className="col-span-full mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                            {field.name}
                            {field.required && <span className="ml-1 text-red-600">*</span>}
                          </label>
                          {field.type === 'textarea' ? (
                            <textarea rows={field.rows || 3}
                              disabled={isReadOnly}
                              className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                              value={formData.categories?.[fieldKey] || ''}
                              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                              placeholder={getPlaceholder(field)} />
                          ) : field.type === 'select' ? (
                            <select
                              disabled={isReadOnly}
                              className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                              value={formData.categories?.[fieldKey] || ''}
                              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                            >
                              <option value="">Select {field.name}...</option>
                              {field.options?.map((option, idx) => <option key={idx} value={option}>{option}</option>)}
                            </select>
                          ) : (
                            <input type="text"
                              disabled={isReadOnly}
                              className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                              value={formData.categories?.[fieldKey] || ''}
                              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                              placeholder={getPlaceholder(field)} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) :
                  visibleFields.map((field, fieldIndex) => {
                    const fieldKey = `${category.name}-${field.name}`;
                    const placeholder = getPlaceholder(field);
                    return (
                      <div key={fieldIndex} className="mb-4 min-w-0">
                        <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                          {field.name}
                          {field.required && <span className="ml-1 text-red-600">*</span>}
                        </label>
                        {field.type === 'select' && field.name === 'Primary ICD-10 Code' && (
                          <div>
                            <ICDSelect
                              value={formData.categories?.[fieldKey] || ''}
                              onChange={(val) => handleInputChange(fieldKey, val)}
                              placeholder={`Select ${field.name}...`}
                              className={`min-w-0 ${field.required && !formData.categories?.[fieldKey] ? 'ring-1 ring-red-300' : ''}`}
                              disabled={isReadOnly}
                            />
                          </div>
                        )}
                        {field.type === 'select' && field.name !== 'Primary ICD-10 Code' && (
                          <select
                            disabled={isReadOnly}
                            className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            value={formData.categories?.[fieldKey] || ''}
                            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          >
                            <option value="">Select {field.name}...</option>
                            {field.options?.map((option, idx) => <option key={idx} value={option}>{option}</option>)}
                          </select>
                        )}
                        {field.type === 'date' && (
                          <input type="date"
                            disabled={isReadOnly}
                            className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            value={formData.categories?.[fieldKey] || ''}
                            onChange={(e) => handleInputChange(fieldKey, e.target.value)} />
                        )}
                        {field.type === 'textarea' && (
                          <textarea rows={field.rows || 3}
                            disabled={isReadOnly}
                            className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            value={formData.categories?.[fieldKey] || ''}
                            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                            placeholder={placeholder} />
                        )}
                        {field.type === 'text' && (
                          <input type="text"
                            disabled={isReadOnly}
                            className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            value={formData.categories?.[fieldKey] || ''}
                            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                            placeholder={placeholder} />
                        )}
                        {field.type === 'checkbox' && (
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              disabled={isReadOnly}
                              id={fieldKey}
                              checked={formData.categories?.[fieldKey] === 'true' || formData.categories?.[fieldKey] === true}
                              onChange={(e) => handleInputChange(fieldKey, e.target.checked ? 'true' : 'false')}
                              className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500`}
                            />
                          </div>
                        )}
                        {field.type === 'custom-icd10-list' && (
                          <div className="space-y-3">
                            <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                              <div className="flex-1 w-full">
                                <ICDSelect
                                  exclude={secondaryICD10Codes}
                                  value={selectedSecondaryICD10}
                                  onChange={(val) => setSelectedSecondaryICD10(val)}
                                  placeholder="Select ICD-10 code..."
                                  className="flex-1 w-full"
                                  disabled={isReadOnly}
                                />
                              </div>
                              <PrimaryButton
                                type="button"
                                onClick={handleAddSecondaryICD10}
                                disabled={!selectedSecondaryICD10 || isReadOnly}
                                className="whitespace-nowrap w-full sm:w-auto"
                              >
                                Add ICD Code
                              </PrimaryButton>
                            </div>
                            {secondaryICD10Codes.length > 0 && (
                              <div className="border border-gray-200 rounded p-3 bg-gray-50">
                                <p className="text-sm font-medium text-gray-700 mb-2">Added Secondary Diagnoses:</p>
                                <ul className="space-y-2">
                                  {secondaryICD10Codes.map((code, idx) => (
                                  <li key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                    <span className="text-sm">{code}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveSecondaryICD10(code)}
                                      disabled={isReadOnly}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          );
        })}
        
      </div>
    </div>
  );
  }
);
NursingAssessmentForm.displayName = 'NursingAssessmentForm';

export default NursingAssessmentForm;
